/* eslint-disable @typescript-eslint/explicit-module-boundary-types */

import { inspect, InspectOptionsStylized } from 'util';

/**
 * K is used here just to keep signature same as normal Map
 */
export class CaseInsensitiveMap<K extends string, V> implements Map<K, V> {
	private readonly _map = new Map<string, [actualKey: K, value: V]>();
	private _normKey(key: K): string {
		return key.toLowerCase();
	}

	constructor(normKey?: (key: K) => string) {
		if (normKey) {
			this._normKey = normKey;
		}
	}

	get [Symbol.toStringTag](): string {
		return 'CaseInsensitiveMap';
	}

	*[Symbol.iterator]() {
		for (const keyAndValue of this._map.values()) {
			yield keyAndValue;
		}
	}

	*keys() {
		for (const [key] of this._map.values()) {
			yield key;
		}
	}

	*values() {
		for (const [, value] of this._map.values()) {
			yield value;
		}
	}

	get size(): number {
		return this._map.size;
	}

	set(key: K, value: V) {
		this._map.set(this._normKey(key), [key, value]);
		return this;
	}

	get(key: K): V | undefined {
		return this._map.get(this._normKey(key))?.[1];
	}

	clear() {
		this._map.clear();
	}

	*entries() {
		for (const keyAndValue of this._map.values()) {
			yield keyAndValue;
		}
	}

	forEach(callbackfn: (value: V, key: K, map: Map<K, V>) => void, thisArg?: any): void {
		this._map.forEach(([key, value], _key) => {
			callbackfn.call(thisArg, value, key, this);
		});
	}

	has(key: K) {
		return this._map.has(this._normKey(key));
	}

	delete(key: K) {
		return this._map.delete(this._normKey(key));
	}

	[inspect.custom](depth: number, options: InspectOptionsStylized, _inspect: any): string {
		if (depth < 0) {
			return options.stylize('[CaseInsensitiveMap]', 'special');
		}

		const inner = _inspect(new Map(this), { ...options, depth });
		return 'CaseInsensitiveMap' + inner.slice(3);
	}
}
